import { NavigateFunction } from 'react-router-dom';
import {
  CREATE_CAMPAIGN,
  UPDATE_CAMPAIGN,
  GET_CAMPAIGN_DETAILS,
  GET_CAMPAIGN_SUMMARY,
} from '../../constants/ActionTypes';
import {
  ICreateCampaignAction,
  IUpdateCampaignAction,
  IGetCampaignDetailsAction,
  IGetCampaignSummaryAction,
} from '../../types/dhwani/campaign.types';

/**
 * Action to create a new campaign
 * @param {NavigateFunction} navigate - Navigation function
 * @param {FormData} payload - Campaign data as FormData
 * @param {boolean} isNext - Whether to navigate to next step
 * @returns {ICreateCampaignAction} Action object
 */
export const createCampaignAction = (
  navigate: NavigateFunction,
  payload: FormData,
  isNext: boolean
): ICreateCampaignAction => ({
  type: CREATE_CAMPAIGN,
  payload: {
    navigate,
    payload,
    isNext,
  },
});

/**
 * Action to update an existing campaign
 * @param {NavigateFunction} navigate - Navigation function
 * @param {string} campaignSlug - Campaign slug to update
 * @param {FormData} payload - Update data as FormData
 * @param {boolean} isNext - Whether to navigate to next step
 * @param {boolean} redirectToCampaignDashboard - Whether to redirect to dashboard
 * @returns {IUpdateCampaignAction} Action object
 */
export const updateCampaignAction = (
  navigate: NavigateFunction,
  campaignSlug: string,
  payload: FormData,
  isNext: boolean,
  redirectToCampaignDashboard: boolean
): IUpdateCampaignAction => ({
  type: UPDATE_CAMPAIGN,
  payload: {
    navigate,
    campaignSlug,
    payload,
    isNext,
    redirectToCampaignDashboard,
  },
});

/**
 * Action to get campaign details
 * @param {string} campaignSlug - Campaign slug to fetch
 * @returns {IGetCampaignDetailsAction} Action object
 */
export const getCampaignDetailsAction = (
  campaignSlug: string
): IGetCampaignDetailsAction => ({
  type: GET_CAMPAIGN_DETAILS,
  payload: {
    campaignSlug,
  },
});

/**
 * Action to reset campaign details
 * @returns {Object} Action object
 */
export const resetCampaignDetailsAction = () => ({
  type: `${GET_CAMPAIGN_DETAILS}_RESET`,
});

/**
 * Action to set campaign details as loading
 * @returns {Object} Action object
 */
export const loadingCampaignDetailsAction = () => ({
  type: `${GET_CAMPAIGN_DETAILS}_LOADING`,
});

/**
 * Action to set campaign details as loaded
 * @returns {Object} Action object
 */
export const loadedCampaignDetailsAction = () => ({
  type: `${GET_CAMPAIGN_DETAILS}_LOADED`,
});

/**
 * Action to get campaign summary
 * @param {string} campaignSlug - Campaign slug to fetch summary for
 * @returns {IGetCampaignSummaryAction} Action object
 */
export const getCampaignSummaryAction = (
  campaignSlug: string
): IGetCampaignSummaryAction => ({
  type: GET_CAMPAIGN_SUMMARY,
  payload: {
    campaignSlug,
  },
});
